import type { BrokerConfig, InstanceMode } from './schema';
import {
  ALLOWED_BRAND_TOKENS,
  VALID_MODES,
  META_MODE,
  TENANT_MODE,
  META_BROKER_REALM,
} from './schema';

const TOKEN_CSS_MAP: Record<string, string> = {
  brandAccent: '--brand-accent',
  brandSurfaceInverted: '--brand-surface-inverted',
  brandWordmarkColor: '--brand-wordmark-color',
};

export async function fetchConfig(): Promise<BrokerConfig> {
  const res = await fetch('/config.json');
  if (!res.ok) throw new Error(`CONFIG_FETCH_FAILED:${res.status}`);
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    throw new Error('CONFIG_PARSE_ERROR');
  }
  return validateConfig(raw);
}

export function validateConfig(raw: unknown): BrokerConfig {
  if (!raw || typeof raw !== 'object') throw new Error('CONFIG_INVALID_SHAPE');
  const c = raw as Record<string, unknown>;

  // instance
  if (!c.instance || typeof c.instance !== 'object') throw new Error('CONFIG_MISSING_INSTANCE');
  const inst = c.instance as Record<string, unknown>;
  if (!VALID_MODES.includes(inst.mode as never)) throw new Error('CONFIG_INVALID_MODE');
  if (typeof inst.name !== 'string') throw new Error('CONFIG_MISSING_INSTANCE_NAME');
  if (typeof inst.slug !== 'string') throw new Error('CONFIG_MISSING_INSTANCE_SLUG');

  // auth
  if (!c.auth || typeof c.auth !== 'object') throw new Error('CONFIG_MISSING_AUTH');
  const auth = c.auth as Record<string, unknown>;
  if (typeof auth.enabled !== 'boolean') throw new Error('CONFIG_INVALID_AUTH_ENABLED');
  if (auth.enabled && !auth.keycloak) throw new Error('CONFIG_AUTH_MISCONFIGURED');

  // ── mode ⇄ auth shape cross-check (decision-2026-07-25-broker-mode-split A3) ──
  // The meta and tenant brokers ship from ONE artifact and are told apart only by
  // this file. Validating the mode string alone is therefore not enough: a config
  // carrying the tenant's realm/client with the meta mode string (or the reverse)
  // would silently resurrect the collision the split exists to remove. So the two
  // declarations — the mode string and the auth/realm shape — must agree, and the
  // deployment must additionally self-identify via instance.tenant.
  assertModeAuthShape(inst.mode as InstanceMode, inst, auth);

  // catalog
  if (!c.catalog || typeof c.catalog !== 'object') throw new Error('CONFIG_MISSING_CATALOG');
  const cat = c.catalog as Record<string, unknown>;
  if (!Array.isArray(cat.tiles)) throw new Error('CONFIG_INVALID_TILES');

  // brandTokens — strip unknown keys
  if (c.brandTokens && typeof c.brandTokens === 'object') {
    const bt = c.brandTokens as Record<string, unknown>;
    for (const key of Object.keys(bt)) {
      if (!ALLOWED_BRAND_TOKENS.includes(key as never)) {
        console.error(`[config] Rejected locked brand token override: ${key}`);
        delete bt[key];
      }
    }
  }

  return raw as BrokerConfig;
}

/**
 * Refuse a config whose mode string and auth/realm shape disagree.
 *
 * Rules (only the two split modes are constrained; tier-0 / reference / tier-1-hub
 * are untouched by the split and pass through):
 *
 *   vertical-ref (meta)      auth.enabled MUST be true
 *                            auth.keycloak.realm MUST be META_BROKER_REALM
 *                            instance.tenant MUST be absent
 *
 *   vertical-tenant          auth.enabled MUST be true
 *                            auth.keycloak.realm MUST NOT be META_BROKER_REALM
 *                            instance.tenant MUST be a non-empty string
 *
 * Concretely: editing the deployed tenant config's mode back to 'vertical-ref' while
 * keeping the tenant realm now fails at startup instead of quietly re-exposing the
 * operator surface on a tenant host.
 */
function assertModeAuthShape(
  mode: InstanceMode,
  inst: Record<string, unknown>,
  auth: Record<string, unknown>,
): void {
  if (mode !== META_MODE && mode !== TENANT_MODE) return;

  if (auth.enabled !== true) throw new Error('CONFIG_MODE_AUTH_MISMATCH');

  const kc = auth.keycloak as Record<string, unknown> | undefined;
  const realm = typeof kc?.realm === 'string' ? kc.realm : '';
  if (!realm) throw new Error('CONFIG_MODE_AUTH_MISMATCH');

  const tenant = inst.tenant;

  if (mode === META_MODE) {
    if (realm !== META_BROKER_REALM) throw new Error('CONFIG_MODE_AUTH_MISMATCH');
    if (tenant !== undefined) throw new Error('CONFIG_MODE_TENANT_MISMATCH');
    return;
  }

  // TENANT_MODE
  if (realm === META_BROKER_REALM) throw new Error('CONFIG_MODE_AUTH_MISMATCH');
  if (typeof tenant !== 'string' || tenant.trim() === '') {
    throw new Error('CONFIG_MODE_TENANT_MISMATCH');
  }
}

export function applyBrandTokens(config: BrokerConfig): void {
  if (!config.brandTokens) return;
  const root = document.documentElement;
  for (const [key, value] of Object.entries(config.brandTokens)) {
    const cssVar = TOKEN_CSS_MAP[key];
    if (cssVar && typeof value === 'string') {
      root.style.setProperty(cssVar, value);
    }
  }
}
